/**
 * The overlay assistant's stream reader.
 *
 * Guarantee about where the answers come from:
 * answers from the user documentation AND the curated feature registry
 * (portal guide); both are maintained, non-live sources — no tenant data.
 * The registry says *where* something is in the portal, the documentation says
 * *why*; the same turn reads both tools, which is why this call sets
 * `engine: 'agent'` (see below).
 *
 * No pinned toolset any more (live test 22.09.): this call sends the same
 * `toolsFilter` as the main chat without a tool selection (`undefined`),
 * because the agent loop treats an exclusively filtered tool like one chosen
 * by the user and passes its output through before the actual answer (see the
 * comment at the call below). Both sources — the documentation and the portal
 * guide (`general/portal-guide`) — reach the turn server-side via
 * `ALWAYS_INCLUDED_TOOL_IDS` instead and are visible to every tenant.
 *
 * It talks to the same request processing as the main chat — `POST
 * /skills/prompt/stream` via `userAPI.streamPromptResponse` — but reads only
 * what it can show. The wire format is text with fenced JSON blocks:
 *
 * ```text
 *   Freier Text …
 *   ```payload
 *   { "endOfMessage": true, "resources": [ … ] }
 *   ```
 *   … weiterer Text
 * ```
 *
 * `ChatExchange` interprets those payloads in full: resources, inline chart
 * placeholders, follow-up chips, file attachments, reconnect cursors, stall
 * detection. Almost none of that exists here. This assistant shows text —
 * and, since stage 2 of the portal guide, the **two guide blocks**
 * (`navigate`, `highlight`, see `src/guide/guideBlocks.ts`), because they need
 * no rendering machinery: one is a button with an address, the other a pointer
 * at an element on the page. Everything else is still discarded deliberately
 * — a reduced reader that silently dropped a resource would be worse than one
 * that never promised to show it.
 *
 * The answer carries `<div class="progress">…</div>` status markup inline; that
 * is separated for display, not here, so the raw text stays intact.
 */

import { isGuideBlock, type GuideBlock } from "../../guide/guideBlocks";
import { userAPI } from "../../UserAPI";

const PAYLOAD_FENCE = "```payload";
const FENCE_END = "```";

export interface OverlayStreamHandlers {
  /** One chunk of answer text — append it. */
  onToken: (chunk: string) => void;
  /** The answer is complete. */
  onDone: () => void;
  onError: (error: Error) => void;
  /**
   * The portal guide blocks from a payload — at most one `navigate` and one
   * `highlight` per answer (CONTRACT.md §7.3). Called as soon as the block has
   * been read, i.e. in stream order; the caller decides what to do with them
   * and when (the button immediately, the pointer and the automatic jump only
   * after `onDone`).
   *
   * Optional, so a caller without the guide needs to know nothing: without
   * this callback the payload is read as before — i.e. not at all.
   */
  onGuideBlocks?: (blocks: GuideBlock[]) => void;
}

export interface OverlayStreamOptions {
  conversationId: string;
  question: string;
  user: { firstName?: string; lastName?: string; email?: string };
  locale: string;
}

/**
 * Start streaming an answer. Returns an abort function.
 *
 * Deliberately no reconnect and no stall detection: the overlay is a short
 * question-and-answer box, and a failed stream costs the user one retry. The
 * main chat needs that machinery because a lost stream there can cost a long
 * agentic run.
 */
export const streamOverlayAnswer = (
  options: OverlayStreamOptions,
  { onToken, onDone, onError, onGuideBlocks }: OverlayStreamHandlers,
): (() => void) => {
  const controller = new AbortController();

  (async () => {
    try {
      const response = await userAPI.streamPromptResponse(
        options.conversationId,
        // The main chat sends `null` here too (its `agent` state is untyped);
        // the parameter is declared as a string, so the cast keeps the wire
        // value identical rather than inventing an empty agent name.
        null as any,
        "Profile1",
        options.question,
        // `engine: 'agent'` instead of the default: on the router path
        // (`AGENT_LOOP_DEFAULT_ENGINE`, default `router`) the judge picks
        // exactly *one* tool per turn — "registry says where, docs say why"
        // would be structurally impossible there.
        // `UserAPI.streamPromptResponse` reads the value from
        // `context.engine` and sends it as a separate field; a string, not a
        // flag, so `auto` can be added later without changing the contract.
        { user: options.user, locale: options.locale, engine: "agent" },
        // No exclusive filter any more (live test 22.09.): the same argument
        // `ChatExchange` sends without a tool selection — there
        // `properties.toolsFilter` is never set on the main chat page
        // (`ChatBot.tsx`), i.e. `undefined`. The agent loop
        // (`agentLoopRunner.ts`) treats a tool pinned like this as one chosen
        // by the user and passes its complete output through verbatim before
        // the actual answer ("the output of a chosen tool goes to the user,
        // not just to the model"): for „Wie lege ich eine Alarmregel an?"
        // unrelated docs text on another topic therefore appeared before the
        // right path. According to the product owner this panel is the same
        // chat as `/chatbot` — so the same tool set, no pin. The docs
        // knowledge base and the portal guide tool (`general/portal-guide`)
        // both reach the turn server-side via `ALWAYS_INCLUDED_TOOL_IDS` plus
        // the forced selection list (`agentLoopContext.ts`) — independent of
        // this filter, and visible to every tenant.
        undefined,
        controller.signal,
      );

      if (!response?.ok || !response.body) {
        onError(new Error(`Stream nicht verfügbar (${response?.status})`));
        return;
      }

      const reader = response.body
        .pipeThrough(new TextDecoderStream("utf-8"))
        .getReader();

      let insidePayload = false;
      let buffer = "";

      /**
       * From a payload, **only** what the panel can actually show is read:
       * the two portal guide blocks (stage 2).
       *
       * Everything else — resources, chips, reconnect cursors — is still
       * discarded. `endOfMessage` also stays unread: text can follow it, and
       * the main chat keeps reading until the stream ends, so this reader does
       * too (`onDone` reports the end). The function consumes the fenced block
       * so its JSON never ends up in the answer.
       *
       * Broken JSON is not a conversation error: the block is discarded, and
       * the answer text carries the path as a Markdown link anyway (text
       * mirroring, CONTRACT.md §7.3).
       */
      const readPayload = (raw: string) => {
        if (!onGuideBlocks) return;
        let payload: any;
        try {
          payload = JSON.parse(raw);
        } catch {
          return;
        }
        if (!Array.isArray(payload?.resources)) return;
        const blocks = payload.resources.filter(isGuideBlock) as GuideBlock[];
        if (blocks.length > 0) onGuideBlocks(blocks);
      };

      /**
       * How much of the tail could still turn into a fence once the next chunk
       * arrives.
       *
       * Fences do not respect network boundaries: a chunk can end in the middle
       * of "```payload". Emitting that tail as answer text puts stray backticks
       * into the output and swallows the fence, so it is held back until the
       * next chunk proves what it is.
       */
      const pendingFenceLength = (text: string): number => {
        const marker = insidePayload ? FENCE_END : PAYLOAD_FENCE;
        const maximum = Math.min(marker.length - 1, text.length);
        for (let length = maximum; length > 0; length -= 1) {
          if (marker.startsWith(text.slice(text.length - length))) return length;
        }
        return 0;
      };

      const drain = (final: boolean) => {
        for (;;) {
          if (insidePayload) {
            const end = buffer.indexOf(FENCE_END);
            if (end === -1) {
              if (final) buffer = "";
              return;
            }
            readPayload(buffer.slice(0, end));
            buffer = buffer.slice(end + FENCE_END.length);
            insidePayload = false;
            continue;
          }

          const start = buffer.indexOf(PAYLOAD_FENCE);
          if (start === -1) {
            const hold = final ? 0 : pendingFenceLength(buffer);
            const emit = buffer.slice(0, buffer.length - hold);
            if (emit) onToken(emit);
            buffer = buffer.slice(buffer.length - hold);
            return;
          }

          if (start > 0) onToken(buffer.slice(0, start));
          buffer = buffer.slice(start + PAYLOAD_FENCE.length);
          insidePayload = true;
        }
      };

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += value ?? "";
        drain(false);
      }

      drain(true);

      onDone();
    } catch (error: any) {
      if (controller.signal.aborted) return;
      onError(error instanceof Error ? error : new Error(String(error)));
    }
  })();

  return () => controller.abort();
};
